import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, Plus, Upload, History, Trash2, Boxes, Radar } from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal, Select, EmptyState } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtRp, fmtNum } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useT } from "../lib/i18n";
import SalesImportWizard from "../components/SalesImportWizard";
import SalesImportHistory from "../components/SalesImportHistory";
import SalesConsumptionReport from "../components/SalesConsumptionReport";

const today = () => new Date().toISOString().slice(0, 10);

export default function Sales() {
  const toast = useToast();
  const { isRole, hasPermission } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [menus, setMenus] = useState([]);
  const [branches, setBranches] = useState([]);
  const [branchFilter, setBranchFilter] = useState("");
  const [manualOpen, setManualOpen] = useState(false);
  const [form, setForm] = useState({ branch_id: "", menu_item_id: "", date: today(), quantity: "", gross_sales: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [consumptionOpen, setConsumptionOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isBranch = isRole("BRANCH_STAFF");
  const canImport = hasPermission("import_sales");
  const canManualEntry = isRole("SUPER_ADMIN", "CENTRAL_PURCHASING", "BRANCH_STAFF");
  const canViewHistory = hasPermission("view_import_history");
  const canViewSales = hasPermission("view_sales");

  const load = () => {
    const qs = !isBranch && branchFilter ? `?branch_id=${branchFilter}` : "";
    api.get(`/sales${qs}`).then(({ data }) => setData(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchFilter]);
  useEffect(() => {
    api.get("/menu-items?active_only=true").then(({ data }) => setMenus(data));
    if (!isBranch) api.get("/branches").then(({ data }) => setBranches(data));
  }, [isBranch]);

  const submitManual = async () => {
    if (!form.menu_item_id || !form.quantity) return toast.error(t("Menu item and quantity required"));
    if (!isBranch && !form.branch_id) return toast.error(t("Select a branch"));
    setBusy(true);
    try {
      await api.post("/sales", { ...form, quantity: Number(form.quantity), gross_sales: form.gross_sales ? Number(form.gross_sales) : null });
      toast.success(t("Sale recorded"));
      setManualOpen(false); setForm({ branch_id: "", menu_item_id: "", date: today(), quantity: "", gross_sales: "" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm(t("Delete this sales row?"))) return;
    try { await api.delete(`/sales/${id}`); toast.success(t("Deleted")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
  };

  if (!data) return <PageLoader />;

  return (
    <div className="animate-fade-up pb-24 lg:pb-2">
      <PageHeader title={t("Sales")} subtitle={t("Daily sales by menu item — manual or Excel/CSV import")} icon={Receipt}>
        {canViewSales && <Button variant="outline" onClick={() => setConsumptionOpen(true)} data-testid="consumption-report-button"><Boxes size={18} /> {t("Consumption")}</Button>}
        {isRole("SUPER_ADMIN", "OWNER", "CENTRAL_PURCHASING", "WAREHOUSE") && <Button variant="outline" onClick={() => navigate("/traceability")} data-testid="sales-trace-button"><Radar size={18} /> Telusur Rantai Pasok</Button>}
        {canViewHistory && <Button variant="outline" onClick={() => setHistoryOpen(true)} data-testid="import-history-button"><History size={18} /> {t("Import History")}</Button>}
        {canImport && <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="import-sales-button"><Upload size={18} /> {t("Import")}</Button>}
        {canManualEntry && <Button onClick={() => setManualOpen(true)} data-testid="add-sale-button"><Plus size={18} /> {t("Add Sale")}</Button>}
      </PageHeader>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        {!isBranch && (
          <Select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} className="w-52" data-testid="sales-branch-filter">
            <option value="">{t("All branches")}</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        )}
        <div className="ml-auto flex gap-4 text-sm">
          <span className="text-ink-mute">{t("Units sold")}: <b className="text-ink">{fmtNum(data.total_qty)}</b></span>
          <span className="text-ink-mute">{t("Gross")}: <b className="text-ink">{fmtRp(data.total_gross)}</b></span>
        </div>
      </div>

      {data.sales.length === 0 ? (
        <EmptyState title={t("No sales recorded")} subtitle={t("Add sales manually or import your daily sales report to enable consumption & variance analysis.")} />
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sales-table">
              <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
                <th className="px-4 py-3">{t("Date")}</th><th className="px-4 py-3">{t("Branch")}</th><th className="px-4 py-3">{t("Menu Item")}</th>
                <th className="px-4 py-3 text-right">{t("Qty")}</th><th className="px-4 py-3 text-right">{t("Gross")}</th><th className="px-4 py-3 text-center">{t("Source")}</th>{!isBranch && <th className="px-4 py-3"></th>}</tr></thead>
              <tbody>
                {data.sales.map((s) => (
                  <tr key={s.id} className="border-b border-line/60 hover:bg-stone-50/60">
                    <td className="px-4 py-2.5">{s.date}</td>
                    <td className="px-4 py-2.5">{s.branch_name}</td>
                    <td className="px-4 py-2.5 font-medium">{s.menu_item_name}</td>
                    <td className="px-4 py-2.5 text-right">{fmtNum(s.quantity)}</td>
                    <td className="px-4 py-2.5 text-right">{s.gross_sales ? fmtRp(s.gross_sales) : "—"}</td>
                    <td className="px-4 py-2.5 text-center"><Badge tone={s.source === "import" ? "info" : "mute"}>{t(s.source)}</Badge></td>
                    {!isBranch && <td className="px-4 py-2.5 text-right"><button onClick={() => del(s.id)} className="text-danger" data-testid={`delete-sale-${s.id}`}><Trash2 size={14} /></button></td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Manual entry */}
      <Modal open={manualOpen} onClose={() => setManualOpen(false)} title={t("Add Sale")}
        footer={<><Button variant="outline" onClick={() => setManualOpen(false)}>{t("Cancel")}</Button><Button onClick={submitManual} loading={busy} data-testid="submit-sale">{t("Save")}</Button></>}>
        <div className="space-y-3">
          {!isBranch && (
            <Select label={t("Branch")} value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} data-testid="sale-branch">
              <option value="">{t("Select branch…")}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </Select>
          )}
          <Select label={t("Menu Item")} value={form.menu_item_id} onChange={(e) => setForm({ ...form, menu_item_id: e.target.value })} data-testid="sale-menu-item">
            <option value="">{t("Select menu item…")}</option>
            {menus.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="cf-label">{t("Date")}</label><input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="cf-input" data-testid="sale-date" /></div>
            <div><label className="cf-label">{t("Quantity Sold")}</label><input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="cf-input" data-testid="sale-qty" /></div>
          </div>
          <div><label className="cf-label">{t("Gross Sales (Rp, optional)")}</label><input type="number" value={form.gross_sales} onChange={(e) => setForm({ ...form, gross_sales: e.target.value })} className="cf-input" data-testid="sale-gross" /></div>
        </div>
      </Modal>

      {/* Import Wizard + History */}
      <SalesImportWizard open={importOpen} onClose={() => setImportOpen(false)} onDone={load} isBranch={isBranch} />
      <SalesImportHistory open={historyOpen} onClose={() => setHistoryOpen(false)} />
      <SalesConsumptionReport open={consumptionOpen} onClose={() => setConsumptionOpen(false)} />
    </div>
  );
}
